import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../../components/commonDesign/Button'
import SydInput from '../../../../components/commonDesign/SydInput'
import SydTextArea from '../../../../components/commonDesign/SydTextArea'
import SydSelect from '../../../../components/commonDesign/SydSelect'
import SydLabel, { hookFormErrorAdapter } from '../../../../components/commonDesign/SydLabel'
import SydDatePicker from '../../../../components/commonDesign/SydDatePicker'
import Loading from '../../../../components/molecules/Loading'
import { useSubmitDenaliAction } from '../../../../api/denaliAssistant'

const useStyles = makeStyles((theme) => ({
  actions: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--text-color-main)',
    fontWeight: 'var(--font-weight-light)',
    lineHeight: 1.6,
    margin: '12px 0'
  },
  actionLink: {
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: 'var(--color-primary-600)'
    }
  },
  threadLink: {
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    textDecoration: 'underline',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      color: 'var(--color-primary-600)'
    }
  },
  actionDisabled: {
    color: 'var(--color-neutral-400)',
    textDecoration: 'line-through',
    cursor: 'not-allowed'
  },
  errorMessage: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic',
    margin: '12px 0'
  },
  formContainer: {
    margin: '16px 0',
    padding: '20px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--dai-color-surface4)',
    fontFamily: 'var(--font-sans)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box'
  },
  formTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    marginBottom: '16px',
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.4
  },
  formField: {
    marginBottom: '16px',
    width: '100%',
    boxSizing: 'border-box',
    '&:last-of-type': {
      marginBottom: 0
    },
    // Override Material-UI generated classes for SydInput, SydTextArea, SydSelect
    // These classes like .makeStyles-sydInput-910 have width: 100% but calculate inconsistently
    '& [class*="makeStyles-sydInput"]': {
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important',
      minWidth: '0 !important'
    },
    '& [class*="makeStyles-sydTextArea"]': {
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important',
      minWidth: '0 !important'
    },
    '& [class*="makeStyles-sydSelect"]': {
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important',
      minWidth: '0 !important'
    },
    // Also target by class name pattern more broadly
    '& [class*="sydInput"]': {
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important'
    },
    '& [class*="sydTextArea"]': {
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important'
    },
    '& [class*="sydSelect"]': {
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box !important'
    }
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'flex-end'
  },
  formError: {
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    marginBottom: '12px'
  },
  formSuccessContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0'
  },
  formSuccessMessage: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--dai-color-text)',
    fontFamily: 'var(--font-sans)'
  },
  formLoadingOverlay: {
    position: 'relative',
    opacity: 0.6,
    pointerEvents: 'none'
  },
  formLoadingIndicator: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
  },
  formCollapsed: {
    padding: '16px 20px',
    transition: 'height 0.3s ease-out'
  }
}))

class ActionType {
  constructor (data) {
    this.data = data
  }

  getLabel () {
    return this.data.label || `[${this.data.type}]`
  }

  isValid () {
    return true
  }

  render () {
    return null
  }
}

class PromptAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.prompt)
  }

  render (classes, onSuggestionClick) {
    return (
      <span
        className={classes.actionLink}
        onClick={() => onSuggestionClick?.(this.data.prompt)}
      >
        {this.data.label}
      </span>
    )
  }
}

class ThreadAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.prompt)
  }

  render (classes) {
    const handleClick = () => {
      const params = new URLSearchParams()
      params.set('prompt', this.data.prompt)
      params.set('focusType', this.data.focus?.type || '')
      params.set('focusSubjectIdentifier', this.data.focus?.subjectIdentifier || '')
      params.set('focusSubjectLabel', this.data.focus?.subjectLabel || '')
      window.open(`/denali-ai/new?${params.toString()}`, '_blank')
    }

    return (
      <span
        className={classes.threadLink}
        onClick={handleClick}
      >
        <span>{this.data.label}</span>
        <Icon name={ICON_NAMES.openInNew} customSize='14px' style={{ marginLeft: '4px', opacity: 0.7 }} />
      </span>
    )
  }
}

class FormAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.api && this.data.actionTraceId && Array.isArray(this.data.fields) && this.data.fields.length > 0)
  }

  render (classes, message) {
    return <FormRenderer data={this.data} classes={classes} message={message} />
  }
}

class UnsupportedAction extends ActionType {
  render (classes, onSuggestionClick) {
    return (
      <span className={classes.actionDisabled}>
        {this.getLabel()}
      </span>
    )
  }
}

const ACTION_TYPE_REGISTRY = {
  prompt: PromptAction,
  thread: ThreadAction,
  form: FormAction
}

function createAction (data) {
  if (!data || typeof data !== 'object' || !data.type) {
    return null
  }

  const ActionClass = ACTION_TYPE_REGISTRY[data.type]
  if (ActionClass) {
    const action = new ActionClass(data)
    return action.isValid() ? action : null
  }

  return new UnsupportedAction(data)
}

function FormRenderer ({ data, classes, message }) {
  const submitAction = useSubmitDenaliAction()
  const [submitError, setSubmitError] = useState(null)

  const isComplete = message?.data?.actions?.[data.actionTraceId]?.actionStatus === 'complete'

  const defaultValues = React.useMemo(() => {
    const values = {}
    data.fields.forEach(field => {
      values[field.key] = field.value !== undefined ? field.value : ''
    })
    return values
  }, [data.fields])

  const form = useForm({
    mode: 'onChange',
    defaultValues
  })

  const handleSubmit = async (formData) => {
    setSubmitError(null)

    const input = {
      api: data.api
    }
    data.fields.forEach(field => {
      const value = formData[field.key]
      if (value !== undefined && value !== null && value !== '') {
        input[field.key] = value
      }
    })

    try {
      await submitAction.mutateAsync({
        actionTraceId: data.actionTraceId,
        input,
        message
      })
    } catch (error) {
      setSubmitError(error?.message || error?.toString() || 'Failed to submit form. Please try again.')
    }
  }

  const submitting = submitAction.isLoading

  const renderField = (field, disabled = false) => {
    const isHidden = field.hidden === true
    const isReadonly = field.readonly === true
    const isRequired = field.required === true
    const fieldName = field.key
    const fieldValue = form.watch(fieldName)
    const fieldError = form.formState.errors[fieldName]

    // Register the field with react-hook-form
    const registerOptions = {}
    if (isRequired) {
      registerOptions.required = 'This field is required'
    }
    if (field.type === 'number') {
      registerOptions.validate = (value) => {
        if (!value && !isRequired) return true
        if (!value && isRequired) return 'This field is required'
        const num = Number(value)
        return !isNaN(num) || 'Must be a valid number'
      }
    }
    const registerProps = form.register(fieldName, registerOptions)

    if (isHidden) {
      return (
        <input
          key={field.key}
          type='hidden'
          {...registerProps}
          defaultValue={field.value}
        />
      )
    }

    // Inline styles wrapper - highest specificity, works regardless of CSS compilation order
    const inputWrapperStyle = {
      width: '100%',
      boxSizing: 'border-box',
      display: 'block'
    }

    if (isReadonly) {
      return (
        <div key={field.key} className={classes.formField}>
          <div style={inputWrapperStyle}>
            <SydLabel label={field.label} required={isRequired}>
              <SydInput
                {...registerProps}
                value={fieldValue ?? field.value ?? ''}
                disabled
                type='input'
                width='100%'
              />
            </SydLabel>
          </div>
        </div>
      )
    }

    const errorMessage = hookFormErrorAdapter(form, { error: fieldError })

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.key} className={classes.formField}>
            <div style={inputWrapperStyle}>
              <SydLabel
                label={field.label}
                required={isRequired}
                error={errorMessage}
              >
                <SydTextArea
                  {...registerProps}
                  value={fieldValue ?? ''}
                  placeholder={field.placeholder}
                  disabled={disabled}
                  inputProps={{ rows: 4, style: { minHeight: 'auto', width: '100%', boxSizing: 'border-box' } }}
                />
              </SydLabel>
            </div>
          </div>
        )

      case 'select': {
        const selectOptions = (field.options || []).map(opt =>
          typeof opt === 'string' ? { value: opt, label: opt } : opt
        )
        return (
          <div key={field.key} className={classes.formField}>
            <div style={inputWrapperStyle}>
              <SydLabel
                label={field.label}
                required={isRequired}
                error={errorMessage}
              >
                <SydSelect
                  {...registerProps}
                  value={fieldValue ?? ''}
                  placeholder={field.placeholder || `Select ${field.label}`}
                  options={selectOptions}
                  disabled={disabled}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </SydLabel>
            </div>
          </div>
        )
      }

      case 'number':
        return (
          <div key={field.key} className={classes.formField}>
            <div style={inputWrapperStyle}>
              <SydLabel
                label={field.label}
                required={isRequired}
                error={errorMessage}
              >
                <SydInput
                  {...registerProps}
                  value={fieldValue ?? ''}
                  type='number'
                  placeholder={field.placeholder}
                  disabled={disabled}
                  width='100%'
                />
              </SydLabel>
            </div>
          </div>
        )

      case 'date':
        return (
          <div key={field.key} className={classes.formField}>
            <div style={inputWrapperStyle}>
              <SydLabel
                label={field.label}
                required={isRequired}
                error={errorMessage}
              >
                <SydDatePicker
                  {...registerProps}
                  value={fieldValue ?? ''}
                  type='date'
                  disabled={disabled}
                />
              </SydLabel>
            </div>
          </div>
        )

      case 'text':
      default:
        return (
          <div key={field.key} className={classes.formField}>
            <div style={inputWrapperStyle}>
              <SydLabel
                label={field.label}
                required={isRequired}
                error={errorMessage}
              >
                <SydInput
                  {...registerProps}
                  value={fieldValue ?? ''}
                  type='input'
                  placeholder={field.placeholder}
                  disabled={disabled}
                  width='100%'
                />
              </SydLabel>
            </div>
          </div>
        )
    }
  }

  if (isComplete) {
    return (
      <div className={clsx(classes.formContainer, classes.formCollapsed)}>
        <div className={classes.formSuccessContainer}>
          <Icon
            name={ICON_NAMES.checkCircle}
            customSize='20px'
            style={{ color: 'var(--color-success)' }}
          />
          <div className={classes.formSuccessMessage}>
            {data.label} completed successfully
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.formContainer}>
      <div className={classes.formTitle}>{data.label}</div>

      {submitError && (
        <div className={classes.formError}>{submitError}</div>
      )}

      <div className={submitting ? classes.formLoadingOverlay : undefined}>
        {submitting && (
          <div className={classes.formLoadingIndicator}>
            <Loading />
          </div>
        )}

        <form onSubmit={form.handleSubmit(handleSubmit)}>
          {data.fields.map(field => renderField(field, submitting))}

          <div className={classes.formActions}>
            <SydButton
              variant='ghost'
              onClick={() => {
                form.reset()
                setSubmitError(null)
              }}
              disabled={submitting}
            >
              Reset
            </SydButton>
            <SydButton
              variant='primary'
              type='submit'
              processing={submitting}
              disabled={!form.formState.isValid || submitting}
            >
              Submit
            </SydButton>
          </div>
        </form>
      </div>
    </div>
  )
}

FormRenderer.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    api: PropTypes.string.isRequired,
    actionTraceId: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.any,
      options: PropTypes.arrayOf(PropTypes.string),
      placeholder: PropTypes.string,
      required: PropTypes.bool,
      hidden: PropTypes.bool,
      readonly: PropTypes.bool
    })).isRequired
  }).isRequired,
  classes: PropTypes.object.isRequired,
  message: PropTypes.object
}

function InlineActions ({ content, onSuggestionClick, message }) {
  const classes = useStyles()

  const parseActions = (text) => {
    try {
      const firstBracket = text.indexOf('[')
      const lastBracket = text.lastIndexOf(']')

      if (firstBracket === -1 || lastBracket === -1 || firstBracket >= lastBracket) {
        return { error: 'Invalid JSON array format', actions: [] }
      }

      const jsonContent = text.substring(firstBracket, lastBracket + 1)
      const parsed = JSON.parse(jsonContent)

      if (!Array.isArray(parsed)) {
        return { error: 'Actions must be a JSON array', actions: [] }
      }

      const actions = []

      for (const item of parsed) {
        const action = createAction(item)
        if (action) {
          actions.push(action)
        }
      }

      return { actions }
    } catch (error) {
      console.error(error)
      return { error: 'Failed to parse actions JSON', actions: [] }
    }
  }

  const { actions, error } = parseActions(content || '')

  if (error) {
    return (
      <div className={classes.errorMessage}>
        I tried to suggest follow up actions, but something went wrong.
      </div>
    )
  }

  if (actions.length === 0) {
    return null
  }

  if (actions.length === 1 && actions[0] instanceof FormAction) {
    return actions[0].render(classes, message)
  }

  const formatText = () => {
    if (actions.length === 1) {
      const action = actions[0]
      const renderProps = action instanceof ThreadAction ? undefined : onSuggestionClick
      return (
        <>
          Would you like to {action.render(classes, renderProps)}?
        </>
      )
    }

    const renderedActions = actions.map((action, index) => {
      const renderProps = action instanceof ThreadAction ? undefined : onSuggestionClick
      return (
        <React.Fragment key={index}>
          {action.render(classes, renderProps)}
          {index < actions.length - 2 && ', '}
          {index === actions.length - 2 && ', or '}
        </React.Fragment>
      )
    })

    return <>Would you like to {renderedActions}?</>
  }

  return (
    <div className={classes.actions}>
      {formatText()}
    </div>
  )
}

InlineActions.propTypes = {
  content: PropTypes.string.isRequired,
  onSuggestionClick: PropTypes.func,
  message: PropTypes.object
}

export default InlineActions
